import { Button, Dialog as ASDialog, Icon } from "armstrong-react";
import React from "react";
import { Location } from "history";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { actions } from "react-redux-form";
import { Dispatch } from "redux";
import tinycolor from "tinycolor2";
import * as WebRoutes from "@travellocal/utils/webRoutes";
import * as PriceUtil from "../../../../../bothends/priceHelpers";
import { Loader, WelcomeDialog } from "../../../../../core/atoms";
import { Nav, NavLink } from "../../../../../core/organisms/nav";
import { apiPromiseHook } from "../../../../../shared/flux/actions/enterHooks";
import * as EnquiryHelper from "../../../../../shared/helpers/enquiryHelpers";
import { GoogleAnalyticsHelper } from "@travellocal/ui";
import { MatchState } from "../../../../../shared/helpers/reduxHelpers";
import { parseColourString } from "../../../../../../../packages/ui/src/utils/uiHelpers";
import { State } from "../../../../../shared/state/reducers";
import { RoutesInvoker } from "../../../../routesInvoker";
import { ActionPromises } from "../../../../state/actionPromises";
import { EnterHookPromises } from "../../../../state/enterHookPromises";
import { EnquiryHeader } from "../components";

import "./wrap.scss";

interface OwnProps {
  location?: Location;
}

const stateToProps = (store: IWebStoreState) => {
  const travellersMissingDetails = EnquiryHelper.getTravellersWithMissingDetails(store.customerEnquiry.data);

  const enquiryState = store.customerEnquiry;
  const quoteId = enquiryState.data?.isConnected && enquiryState.data?.currentQuoteId;
  const currentItineraryState = quoteId
    ? store.customerItineraries.data[quoteId]
    : new State<IEnquiryItinerarySummary>();

  return {
    userState: store.user,
    enquiryCountry: store.country,
    enquiryState,
    currentItineraryState,
    travellersMissingDetails,
  };
};

const dispatchToProps = (dispatch: Dispatch<IWebStoreState>) => ({
  clearCurrentEnquiry: () => dispatch(ActionPromises.clearCustomerEnquiry()),
  requestCancellation: (req: IEnquiryCancellationRequestUpdate) => dispatch(ActionPromises.requestCancellation(req)),
  reloadEnquiry: (enquiryId: number) => dispatch(ActionPromises.enquiryById(enquiryId)),
  clearCurrentReviewForm: () => dispatch(actions.reset("forms.review")),
});

type Props = OwnProps & ReturnType<typeof dispatchToProps> & ReturnType<typeof stateToProps>;

interface LocalState {
  firstTime?: boolean;
  subD?: boolean;
  cancellationDialogOpen?: boolean;
  cancellationReason?: string;
}

class CustomerEnquiryIndexComponent extends React.Component<Props, LocalState> {
  constructor(props) {
    super(props);

    this.state = {
      cancellationDialogOpen: false,
      cancellationReason: "",
      firstTime: false,
      subD: false,
    };
  }

  private requestCancellation = async () => {
    await this.props.requestCancellation({
      id: this.props.enquiryState.data.id,
      reason: this.state.cancellationReason
    });

    this.setState({ cancellationDialogOpen: false });
    this.props.reloadEnquiry(this.props.enquiryState.data.id);
  }

  public UNSAFE_componentWillMount() {
    if (this.props.location?.query.firstTime) {
      this.setState({ firstTime: true });
    }
  }

  public componentWillUnmount() {
    this.props.clearCurrentEnquiry();
    this.props.clearCurrentReviewForm();
  }

  public render() {
    const { userState, enquiryState, currentItineraryState, enquiryCountry } = this.props;
    const rootStyle: React.CSSProperties = {};
    const country = enquiryCountry?.data;

    if (!userState.data) {
      return <Loader />;
    }

    if (country && enquiryCountry?.status === "OK") {
      // Colour certain page elements with the hero image colour
      const accentColour = country.heroImage ? parseColourString(country.heroImage.colour) : undefined;

      if (accentColour) {
        const c: tinycolor.ColorFormats.RGBA = tinycolor(accentColour).toRgb();
        rootStyle["--page-accent"] = accentColour;
        rootStyle["--page-accent-rgb"] = `${c.r}, ${c.g}, ${c.b}`;
      }
    }

    return (
      <div className="customer-single-enquiry" style={rootStyle}>
        <Helmet title={`${userState.data.firstName}'s trip`} />

        {this.state.firstTime && MatchState(enquiryCountry, {
          none: () => <Loader />,
          data: () => {
            return (
              <WelcomeDialog
                image={country.heroImage}
                isOpen={this.state.firstTime}
                onClose={() => this.setState({ firstTime: false })}
              />
            );
          },
        })}

        <ASDialog title="Request cancellation"
          width={550}
          isOpen={this.state.cancellationDialogOpen}
          onClose={() => this.setState({ cancellationDialogOpen: false })}
          footerContent={(
            <Button
              onClick={() => this.requestCancellation()}
              disabled={!this.state.cancellationReason} className="bg-negative">
              Submit request
            </Button>
          )}>
          <p className="p-bottom-small">Please give a reason for requesting a cancellation of your booking below: </p>
          <textarea
            value={this.state.cancellationReason}
            onChange={(e) => this.setState({ cancellationReason: e.target.value })}
          />
        </ASDialog>

        {MatchState(enquiryState, {
          none: error => {
            if (error) {
              return (
                <EnquiryHeader>
                  <h1>Sorry, something went wrong</h1>
                  <p>We couldn&apos;t load your enquiry details. Please try again later.</p>
                </EnquiryHeader>
              );
            }
            else {
              return (
                <EnquiryHeader>
                  <h1>Loading your trip</h1>
                  <Loader />
                  <p>Please wait...</p>
                </EnquiryHeader>
              );
            }
          },
          data: enquiry => {
            const locationImage = this.props.enquiryCountry.data?.heroImage?.smallUrl;
            const locationName = currentItineraryState?.data?.primaryLocation?.name
              ?? enquiry.location?.name
              ?? "";

            // TODO: Removed until we decide where this should live
            // {this.canRequestCancellation() &&
            //   <p><small><a onClick={() => this.setState({ cancellationDialogOpen: true })}>
            //      Request booking cancellation
            //   </a></small ></p >
            // }

            return (
              <EnquiryHeader locationImage={locationImage} locationName={locationName} />
            );
          },
        })}

        {MatchState(enquiryState, {
          data: enquiry => {
            if (enquiry.cancellationStatus === "cancelled") {
              return;
            }

            const hasItineraryQuote = enquiry.isConnected && enquiry.currentQuoteId != null;
            const isFullyPaid = enquiry.isConnected && hasItineraryQuote
              ? PriceUtil.isZero(PriceUtil.subtract(enquiry.currentItinerarySummary.totalPrice, enquiry.totalPaid))
              : true;

            const showTravellerDetails = [
              EnquiryStatus.Booked,
              EnquiryStatus.FullyPaid,
              EnquiryStatus.InProgress,
              EnquiryStatus.Complete
            ].some(status => status === enquiry.enquiryStatus);

            return (
              <Nav className="nav-marketing-inline">
                <NavLink
                  icon={Icon.Icomoon.home}
                  to={RoutesInvoker.customerEnquiriesIndex(enquiryState.data.id)}
                  title="Home"
                  onlyActiveOnIndex={true}
                  onClick={() => GoogleAnalyticsHelper.logEvent("Tab", "Click", "Home tab")}
                />

                <NavLink
                  icon={Icon.Icomoon.bubbles10}
                  to={WebRoutes.customerEnquiryMessaging(enquiryState.data.id)}
                  title="Messages"
                  onClick={() => GoogleAnalyticsHelper.logEvent("Tab", "Click", "Messages tab")}
                />

                {hasItineraryQuote &&
                  <NavLink
                    icon={Icon.Icomoon.map2}
                    to={WebRoutes.customerEnquiryItinerary(enquiryState.data.id, enquiryState.data.currentQuoteId)}
                    title="Itinerary"
                    onClick={() => GoogleAnalyticsHelper.logEvent("Tab", "Click", "Itinerary tab")}
                  />
                }

                {hasItineraryQuote &&
                  <NavLink
                    icon={Icon.Icomoon.cart3}
                    className={!isFullyPaid ? "tab-needs-attention" : ""}
                    to={WebRoutes.customerEnquiryPayment(enquiryState.data.id)}
                    title="Payment"
                    onClick={() => GoogleAnalyticsHelper.logEvent("Tab", "Click", "Payment tab")}
                  />
                }

                {showTravellerDetails &&
                  <NavLink
                    icon={Icon.Icomoon.list2}
                    className={this.props.travellersMissingDetails.length > 0 ? "tab-needs-attention" : ""}
                    to={RoutesInvoker.customerEnquiryTravellerDetails(enquiryState.data.id)}
                    title="Traveller details"
                  />
                }

                {enquiry.enquiryStatus === EnquiryStatus.Complete &&
                  <NavLink
                    icon={Icon.Icomoon.starFull}
                    to={RoutesInvoker.customerEnquiryReview(enquiryState.data.id)}
                    title="Review"
                  />
                }
              </Nav>
            );
          },
        })}

        {MatchState(enquiryState, {
          none: () => <Loader />,
          data: enquiry => {
            if (enquiry.enquiryStatus === EnquiryStatus.Cancelled) {
              return (
                <div className="width-wrapper t-align-center p-top-medium p-bottom-medium">
                  <h1>This trip has been cancelled</h1>
                  <p>Because this trip has been cancelled, you can no longer update or view any details.</p>
                </div>
              );
            }

            return this.props.children;
          },
        })}
      </div>
    );
  }
}

export const CustomerEnquiryIndex = connect(stateToProps, dispatchToProps)(CustomerEnquiryIndexComponent);

export const customerEnquiryIndexOnEnter = apiPromiseHook(
  EnterHookPromises.customerEnquiryData("enquiryId"),
  EnterHookPromises.allCardTypes(),
);
